import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { uploadService } from "@/lib/api/upload";
import type { GuideContent, GuideCreate } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const schema = z.object({
  category: z.string().min(1, "카테고리를 입력하세요"),
  title: z.string().min(1, "제목을 입력하세요"),
  body: z.string().min(1, "내용을 입력하세요"),
  image_urls: z.array(z.string()).max(5),
  is_published: z.boolean(),
  show_as_button: z.boolean(),
  button_order: z.number({ message: "숫자를 입력하세요" }).int("정수를 입력하세요"),
});

type FormValues = z.infer<typeof schema>;

interface GuideFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guide?: GuideContent | null;
  isPending?: boolean;
  onSubmit: (values: GuideCreate) => void;
}

export function GuideFormDialog({
  open,
  onOpenChange,
  guide,
  isPending,
  onSubmit,
}: GuideFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const urls = watch("image_urls") ?? [];
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    setUploading(true);
    try {
      const next = [...urls];
      for (const f of files) {
        if (next.length >= 5) break;
        const { url } = await uploadService.upload(f);
        next.push(url);
        setValue("image_urls", next, { shouldDirty: true });
      }
    } catch {
      toast.error("이미지 업로드에 실패했습니다.");
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (i: number) =>
    setValue(
      "image_urls",
      urls.filter((_, idx) => idx !== i),
      { shouldDirty: true }
    );

  useEffect(() => {
    if (open) {
      reset({
        category: guide?.category ?? "",
        title: guide?.title ?? "",
        body: guide?.body ?? "",
        image_urls: guide?.image_urls ?? [],
        is_published: guide?.is_published ?? true,
        show_as_button: guide?.show_as_button ?? true,
        button_order: guide?.button_order ?? 0,
      });
    }
  }, [open, guide, reset]);

  const submit = handleSubmit((values) => {
    onSubmit({
      category: values.category.trim(),
      title: values.title.trim(),
      body: values.body,
      image_urls: values.image_urls,
      is_published: values.is_published,
      show_as_button: values.show_as_button,
      button_order: values.button_order,
    });
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{guide ? "가이드 수정" : "새 가이드 · FAQ"}</DialogTitle>
          <DialogDescription>마크다운 형식으로 작성할 수 있습니다.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="guide-category">카테고리 *</Label>
              <Input id="guide-category" {...register("category")} placeholder="체크인" />
              {errors.category ? (
                <p className="text-xs text-destructive">{errors.category.message}</p>
              ) : null}
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="guide-title">제목 *</Label>
              <Input id="guide-title" {...register("title")} placeholder="체크인 방법" />
              {errors.title ? (
                <p className="text-xs text-destructive">{errors.title.message}</p>
              ) : null}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="guide-body">내용 *</Label>
            <Textarea
              id="guide-body"
              {...register("body")}
              className="min-h-[140px]"
              placeholder="안내 내용을 입력하세요"
            />
            {errors.body ? <p className="text-xs text-destructive">{errors.body.message}</p> : null}
          </div>
          <div className="space-y-1.5">
            <Label>안내 이미지 (최대 5장)</Label>
            <div className="flex flex-wrap gap-2">
              {urls.map((u, i) => (
                <div key={u} className="relative h-20 w-20">
                  <img src={u} alt="" className="h-20 w-20 rounded object-cover" />
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    className="absolute -right-2 -top-2 rounded-full bg-black/70 px-1.5 text-xs text-white"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={onPick}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading || urls.length >= 5}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? "업로드 중…" : "이미지 추가"}
            </Button>
            <p className="text-xs text-muted-foreground">
              이 가이드로 답변할 때 챗봇이 답변과 함께 보여줍니다. ({urls.length}/5)
            </p>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border/70 px-3 py-2.5">
            <Label htmlFor="guide-published">게시 상태</Label>
            <Controller
              control={control}
              name="is_published"
              render={({ field }) => (
                <Switch
                  id="guide-published"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border/70 px-3 py-2.5">
            <div className="space-y-0.5">
              <Label htmlFor="guide-show-button">빠른 질문 버튼으로 노출</Label>
              <p className="text-xs text-muted-foreground">
                게스트 채팅 입력창 위에 이 제목을 버튼으로 보여줍니다.
              </p>
            </div>
            <Controller
              control={control}
              name="show_as_button"
              render={({ field }) => (
                <Switch
                  id="guide-show-button"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="guide-button-order">버튼 정렬 순서</Label>
            <Input
              id="guide-button-order"
              type="number"
              {...register("button_order", { valueAsNumber: true })}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground">
              숫자가 작을수록 먼저 표시됩니다 (기본 0).
            </p>
            {errors.button_order ? (
              <p className="text-xs text-destructive">{errors.button_order.message}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              취소
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {guide ? "저장" : "등록"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
